
import MBaseModal from "@/components/MBaseModal.vue";
import MButton from "@/components/MButton.vue";
import MSelectBox from "@/components/form/MSelectBox.vue";
import MTextField from "@/components/form/MTextField.vue";
import MIconFileInput from "@/components/form/MIconFileInput.vue";
import MsStudentPicker from "@/components/student/MsStudentPicker.vue";
import MTextArea from "@/components/form/MTextArea.vue";
import MIcon from "@/components/MIcon.vue";
import store from "@/store";
import { Options, Vue } from "vue-class-component";
import { Student } from "@/entities/student";
import {
  Journal,
  JournalData,
  JournalLabel,
  JournalReflectionItem,
  JournalReflectionType,
  TutorAccessType
} from "@/entities/journal";
import { v4 as uuid } from "uuid";
import { createJournal } from "@/api/journal";
import dayjs from "dayjs";
import { State } from "@/store/helper";

/*
NOTE: これはプレースホルダの表示用。実際に送られるのは journal_ai が持つ
同じ文面なので、編集する時は functions_gen2 側も更新する必要がある
*/
const DEFAULT_PROMPT_TEMPLATE = `#役割
 あなたは生徒指導の先生です。
 ジャーナルという「各項目で生徒自身が学習状況を記載したカード」を元にポジティブなコメントを返し学習を促進させる役割です。

#指示
 #ジャーナルの内容が送られます。それらを分析したのち以下文章を返してください。
「学習おつかれさま！
##コメントがここに入る##」

#制約条件
・ポジティブなコメントとする
・日付での変化に言及する
・コメントは最大500文字までとする

#ジャーナルの内容`;

@Options({
  components: {
    MBaseModal,
    MButton,
    MSelectBox,
    MIconFileInput,
    MTextField,
    MsStudentPicker,
    MIcon,
    MTextArea
  },
  emits: ["close", "syncAddition"],
  props: {
    students: Array
  }
})
export default class JournalAddModal extends Vue {
  @State("allStudentsFetched") allStudentsFetched?: boolean;

  students: Student[] = [];

  title = "";
  titleEntered = false;
  reflectionTypes: { value: JournalReflectionType; label: string }[] = [
    { value: "text", label: "テキスト" },
    { value: "graph", label: "数値" }
  ];
  reflectionItems: JournalReflectionItem[] = [
    this.generateDefaultReflectionItem()
  ];
  availableToAllStudents = false; // 編集不可とし必ずfalseで保存する方針(編集できる箇所をコメントアウトしています)
  selectedStudents: Student[] = [];
  description = "";
  label: JournalLabel = {
    name: "",
    description: ""
  };
  tutorAccess: TutorAccessType = "publicToOwner";
  prompt = DEFAULT_PROMPT_TEMPLATE;
  promptEntered = false;
  includeChatInPrompt = false;

  //タイトル
  get validTitle(): boolean {
    return this.title.length > 0;
  }

  //振り返り項目
  get validReflectionItems(): boolean {
    if (this.reflectionItems.length < 1) return false;

    for (const item of this.reflectionItems) {
      if (!this.validReflectionItem(item)) {
        return false;
      }
    }

    return true;
  }

  validReflectionItem(item: JournalReflectionItem): boolean {
    return !!item.title && !!item.type && !!item.uid;
  }

  // textタイプは必ず1個以上登録が必要とする
  get hasOneOrMoreTextType(): boolean {
    return this.reflectionItems.some(item => item.type === "text");
  }

  //description
  get validDescription(): boolean {
    return true;
  }

  //label
  get validLabel(): boolean {
    return true;
  }

  //tutorAccess
  get validTutorAccess() {
    return ["private", "public", "publicToOwner"].includes(this.tutorAccess);
  }

  //prompt
  get validPrompt(): boolean {
    return this.prompt.length > 0;
  }

  //total validation
  get validData(): boolean {
    return (
      this.validTitle &&
      this.validReflectionItems &&
      this.hasOneOrMoreTextType &&
      this.validDescription &&
      this.validLabel &&
      this.validTutorAccess &&
      this.validPrompt
    );
  }

  //utils
  updateSelectedStudents(students: Student[]) {
    if (students.length === 0) {
      this.selectedStudents = [];
      return;
    }

    this.selectedStudents = [...students];
  }

  updateReflectionItemTitle(val: string, index: number) {
    this.reflectionItems[index].title = val;
  }

  updateReflectionItemType(val: JournalReflectionType, index: number) {
    this.reflectionItems[index].type = val;
  }

  generateDefaultReflectionItem(): JournalReflectionItem {
    return {
      title: "",
      type: "text",
      createdAt: 0,
      uid: uuid()
    };
  }

  addEmptyReflectionItem() {
    this.reflectionItems.push(this.generateDefaultReflectionItem());
  }

  removeReflectionItem(index: number) {
    if (this.reflectionItems.length <= 1) return;
    this.reflectionItems.splice(index, 1);
  }

  //emit
  close() {
    this.$emit("close");
  }

  syncAddition(data: Journal) {
    this.$emit("syncAddition", data);
  }

  //登録
  async registerJournal({ asDraft = false }: { asDraft: boolean }) {
    if (!this.validData) return;
    if (!store.state.school) return;
    if (!store.state.tutor) return;

    const now = dayjs().unix();
    const tutorId = store.state.tutor.config.data.id;

    const data: JournalData = {
      title: this.title,
      description: this.description,
      label: this.label,
      tutorAccess: this.tutorAccess,
      journalReflectionItems: this.reflectionItems.map(item => ({
        ...item,
        createdAt: dayjs().unix()
      })),
      availableToAllStudents: this.availableToAllStudents,
      studentIds: this.availableToAllStudents
        ? []
        : this.selectedStudents.map(s => s.ref.id),
      isCompleted: false,
      isDeleted: false,
      createdAt: now,
      createdBy: tutorId,
      isDraft: asDraft,
      publishedAt: asDraft ? null : now,
      publishedBy: tutorId,
      prompt: this.prompt,
      includeChatDataInPrompt: this.includeChatInPrompt
    };

    try {
      store.commit("START_LOADING", "ジャーナル登録中...");
      const ref = await createJournal(store.state.school.ref, data);
      alert(
        asDraft
          ? "下書きとしてジャーナルを登録しました。"
          : "ジャーナルを登録しました。"
      );
      this.syncAddition({ data, ref });
      this.close();
      store.commit("END_LOADING");
    } catch (e) {
      console.error(e);
      alert(
        "ジャーナルの登録に失敗しました。" +
          "\n" +
          (e instanceof Error ? e.message : "")
      );
    } finally {
      store.commit("END_LOADING");
    }
  }
}
